import { Button, Search, Input, Toggle, Modal, DataGrid, Icon } from '@hydrodigital/design-system';
import { Form, Tooltip, notification } from 'antd';
import useDebounce from '@/hooks/useDebounce';
import dayjs from 'dayjs';
import { FC, useEffect, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import LocationCombobox from '../../components/General/LocationCombobox';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import BlockService from '../../domain/BlockDiagram/BlockService';

import { AllBlocks } from '@/domain/BlockDiagram/Block';
import './index.scss';

const blockService = new BlockService();

type LayoutType = Parameters<typeof Form>[0]['layout'];

/**
 * @author
 * @function @BlockDiagramView
 **/

const BlockDiagramView: FC = () => {
	const [block, setBlock] = useState<any | null>(null);
	const security = localStorage.getItem('security');
	const organizationId = security ? JSON.parse(security).organizationId : '';
	const [form] = Form.useForm();
	const [showNewModal, setShowNewModal] = useState(false);
	const [showConfirmDelete, setShowConfirmDelete] = useState(false);
	const [selectedDiagram, setSelectedDiagram] = useState<AllBlocks | null>(null);
	const [paginationState, setPaginationState] = useState({
		pageIndex: 0,
		pageSize: 10,
	});
	const [totalRows, setTotalRows] = useState(0);
	const [query, setQuery] = useState<string>('');
	const navigate = useNavigate();

	const debouncedQuery = useDebounce(query, 500);

	const handleGetBlock = () => {
		const res = blockService.getBlock(
			organizationId,
			paginationState.pageIndex + 1,
			paginationState.pageSize,
			debouncedQuery || null
		);
		res
			.then((response) => {
				setTotalRows(response.totalRecords);
				setBlock(response.allBlocksResponseDTOList);
			})
			.catch((error) => {
				console.error(error);
			});
	};

	useEffect(() => {
		handleGetBlock();
	}, [paginationState.pageIndex, paginationState.pageSize, debouncedQuery]);

	useEffect(() => {
		setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
	}, [debouncedQuery]);

	const columns = [
		{
			accessorKey: 'name',
			header: 'Diagram name',
			cell: (cellContext: any) => (
				<span className="table-data-gis">{cellContext.getValue()}</span>
			),
		},
		{
			accessorKey: 'location',
			header: 'Location',
			cell: (cellContext: any) => <span>{cellContext.getValue()}</span>,
		},
		{
			accessorKey: 'createdBy',
			header: 'Created by',
			cell: (cellContext: any) => {
				const row = cellContext.row.original as AllBlocks;
				return (
					<div className="flex column">
						<span
							style={{
								color: '#232B36',
								fontSize: '14px',
								fontWeight: '500',
							}}
						>
							{row.createdBy}
						</span>
						<span
							style={{
								color: '#74788D',
							}}
						>
							{dayjs(row.createdAt).format('YYYY/MM/DD [at] hh:mm a')}
						</span>
					</div>
				);
			},
		},
		{
			accessorKey: 'enabled',
			header: 'In use',
			cell: (cellContext: any) => {
				const row = cellContext.row.original as AllBlocks;
				return (
					<div className="flex">
						<Toggle
							key={`toggle-${row.id}-${row.enabled}`}
							defaultChecked={row.enabled}
							checkedText="ON"
							uncheckedText="OFF"
							size="small"
							onChange={(e: ChangeEvent<HTMLInputElement>) => {
								handleChangeEnabled(row, e.target.checked);
							}}
						/>
					</div>
				);
			},
		},
		{
			accessorKey: 'actions',
			header: 'Actions',
			cell: (cellContext: any) => {
				const row = cellContext.row.original as AllBlocks;
				return (
					<div className="flex" style={{ gap: '12px' }}>
						<Tooltip title="Edit">
							<span
								style={{ cursor: 'pointer', display: 'inline-flex' }}
								onClick={() => {
									navigate(`/diagram-block/${row.locationId}/${row.id}?isEdit=true`);
								}}
							>
								<Icon name="edit-alt" size="m" hoverable />
							</span>
						</Tooltip>
						<Tooltip title="Delete">
							<span
								style={{ cursor: 'pointer', display: 'inline-flex' }}
								onClick={() => {
									setSelectedDiagram(row);
									setShowConfirmDelete(true);
								}}
							>
								<Icon name="trash-alt" size="m" hoverable />
							</span>
						</Tooltip>
					</div>
				);
			},
		},
	];

	const handleChangeEnabled = async (record: AllBlocks, enabled: boolean) => {
		if (enabled) {
			await blockService.enableInUseBlockDiagram(record);
		} else {
			await blockService.disableInuseBlockDiagram(record);
		}
		// Actualizar el estado local para reflejar el cambio en el DataGrid
		setBlock((prevList: any) =>
			prevList.map((item: AllBlocks) =>
				item.id === record.id ? { ...item, enabled } : item,
			),
		);
	};

	const handleClickSelect = (value: any) => {
		form.setFieldsValue({ location: value });
	};

	const onFinish = async (values: AllBlocks) => {
		// console.log("values", values);
		// handleAddInfrastructureParemeters(values);

		const respUpdate: any = await blockService.updateBlockDiagram(
			organizationId,
			values.location,
			values,
		);
		if (respUpdate.success) {
			notification.success({
				message: 'Successful registration',
				description: 'Block diagram created successfully',
			});
			const blockId = JSON.parse(respUpdate.data).blockId;
			navigate(`/diagram-block/${values.location}/${blockId}?isEdit=true`);
			// form.resetFields();
			// setShowNewModal(false);
			// handleGetBlock();
		}

		// isModalOpen(false);
	};

	const onFinishFailed = (errorInfo: any) => {
		console.log('Failed:', errorInfo);
	};

	const deleteBlockDiagram = async (id: any) => {
		await blockService.deleteBlockDiagram(id);
		notification.success({
			message: 'Successful deletion',
			description: 'Block diagram deleted successfully',
			placement: 'topRight',
		});
		await handleGetBlock();
		setShowConfirmDelete(false);
	};

	return (
		<div className="px-5 flex flex-col" style={{ height: 'calc(100vh - 140px)' }}>
			<div className="text-title-page mb-2">Block Diagrams</div>
			<div className="flex justify-between" style={{ flexShrink: 0 }}>
				<div style={{ width: 200, minWidth: 200 }}>
					<Search
						placeholder="Search"
						size="small"
						value={query}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setQuery(e.target.value);
						}}
						showClearButton={true}
						onClear={() => {
							setQuery('');
						}}
						style={{ width: '100%' }}
					/>
				</div>
				<div>
					<Button
						label={'Add diagram'}
						variant="secondary"
						size="small"
						iconName="plus-circle"
						onClick={() => {
							setShowNewModal(true);
						}}
					/>
				</div>
			</div>

			<DataGrid
				columns={columns}
				data={block || []}
				className="mt-3"
				stickyHeader={true}
				maxHeight="calc(100vh - 260px)"
				pagination={{
					enabled: true,
					manualPagination: true,
					rowCount: totalRows,
					defaultPageSize: 10,
					pageSizeOptions: [10, 20, 50],
				}}
				paginationState={paginationState}
				onPaginationChange={setPaginationState}
			/>
			<Modal
				isOpen={showNewModal}
				onClose={() => {
					setShowNewModal(false);
					form.resetFields();
				}}
				size="xs"
				showCloseButton={false}
			>
				<Modal.Header>
					<Modal.Title text="CREATE DIAGRAM" />
				</Modal.Header>

				<Modal.Content>
					<Form
						layout={'vertical'}
						form={form}
						onFinish={onFinish}
						onFinishFailed={onFinishFailed}
						style={{ maxWidth: 600 }}
					>
						<Form.Item
							label="Diagram name"
							name="name"
							rules={[
								{
									required: true,
								},
							]}
						>
							<Input placeholder="" size="small" />
						</Form.Item>
						<Form.Item
							label="Select location"
							name="location"
							rules={[
								{
									required: true,
								},
							]}
						>
							<LocationCombobox
								value={form.getFieldValue('location') || null}
								onValueChange={(val) => handleClickSelect(val)}
							/>
						</Form.Item>
						<Form.Item
							label="Enabled"
							name="enabled"
							rules={[
								{
									required: true,
								},
							]}
							valuePropName="checked"
						>
							<Toggle size="small" />
						</Form.Item>
					</Form>
				</Modal.Content>

				<Modal.Footer className="flex justify-between">
					<Button
						label="Accept"
						variant="primary"
						showIcon={false}
						size="small"
						onClick={() => form.submit()}
					/>

					<Button
						label="Cancel"
						variant="secondary"
						showIcon={false}
						size="small"
						onClick={() => setShowNewModal(false)}
					/>
				</Modal.Footer>
			</Modal>
			<ConfirmationModal
				title="Delete Block Diagram?"
				message="Are you sure to delete it?"
				showModal={showConfirmDelete}
				onAccept={async () => {
					if (selectedDiagram) {
						deleteBlockDiagram(selectedDiagram?.id);
					}
				}}
				oncancel={() => {
					setShowConfirmDelete(false);
				}}
			/>
		</div>
	);
};
export default BlockDiagramView;
